import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useOrganization } from "./useOrganization";

interface PlayerCoinsRow {
  balance: number;
  lifetime_earned: number;
  user_id: string;
  organization_id: string;
}

interface CoinTransactionRow {
  id: string;
  user_id: string;
  organization_id: string;
  amount: number;
  type: string;
  reference_id: string | null;
  description: string | null;
  created_at: string;
}

export function usePlayerCoins() {
  const { user } = useAuth();
  const { organization } = useOrganization();

  const { data: coins, ...rest } = useQuery({
    queryKey: ["player-coins", user?.id, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("player_coins" as any)
        .select("*")
        .eq("user_id", user!.id)
        .eq("organization_id", organization!.id)
        .maybeSingle();
      if (error) throw error;
      return data as PlayerCoinsRow | null;
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ["coin-transactions", user?.id, organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coin_transactions" as any)
        .select("*")
        .eq("user_id", user!.id)
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data as CoinTransactionRow[]) ?? [];
    },
    enabled: !!user?.id && !!organization?.id,
  });

  return {
    balance: coins?.balance ?? 0,
    lifetimeEarned: coins?.lifetime_earned ?? 0,
    transactions,
    ...rest,
  };
}
